import type { Exercise, Lesson } from '@fluent/types';
import type { MatchVerdict } from './matchAnswer.js';

/**
 * Lesson engine state machine.
 *
 * Phases:
 *   - `answering`   — user is working on `exercises[index]`. Input is live.
 *   - `checked`     — user submitted a non-close verdict; banner visible.
 *   - `complete`    — lesson finished.
 *
 * The FIRST `close` verdict (Levenshtein-1 typo) does NOT transition phase —
 * the UI shows a gentle nudge and the user keeps typing. Rationale: matches
 * PLAN.md rule that close answers never fail an attempt. The SECOND close on
 * the same exercise does close it out (as correct, no first-try bonus): the
 * nudge can't say what is wrong, so retrying blind is a dead end.
 *
 * Mistakes come back (2026-10-07, GRAMMAR-REVIEW § 2): every exercise answered
 * WRONG on the first pass is asked once more after the last exercise. The
 * play order is derived from `results` (see playOrder), so `index` is a
 * position in that order, GO_BACK stays a plain slice, and a saved session
 * resumes into the retries without storing anything new. Only the first pass
 * scores — retries are practice (calculateStats).
 */

export type LessonPhase = 'answering' | 'checked' | 'complete';

/** Per-exercise log. One row per exercise, appended when the exercise closes. */
export interface ExerciseResult {
  exerciseId: string;
  /** User got it right with no wrong submissions beforehand. */
  firstTry: boolean;
  /** Final recorded verdict. A second `close` records 'correct' (see SUBMIT). */
  verdict: 'correct' | 'wrong';
}

export interface LessonState {
  phase: LessonPhase;
  /** Index into `lesson.exercises`. Clamped to length during `complete`. */
  index: number;
  /** Immutable ledger of closed exercises. */
  results: ExerciseResult[];
  /** Wrong submissions on the current exercise — resets on NEXT. */
  wrongOnCurrent: number;
  /** `close` submissions on the current exercise — resets on NEXT. */
  closeOnCurrent: number;
  /** Last verdict we exposed — null while pristine. */
  lastVerdict: MatchVerdict | null;
  /** Epoch ms when the lesson started. Used for the completion time stat. */
  startedAt: number;
  /** Epoch ms when the lesson completed — null until complete. */
  completedAt: number | null;
}

export type LessonAction =
  | { type: 'SUBMIT'; verdict: MatchVerdict }
  | { type: 'MARK_WRONG_ATTEMPT' }
  | { type: 'ADVANCE_INTRO' }
  | { type: 'NEXT' }
  | { type: 'GO_BACK' }
  | { type: 'RESTART' };

export function initLessonState(_lesson: Lesson, now: number = Date.now()): LessonState {
  return {
    phase: 'answering',
    index: 0,
    results: [],
    wrongOnCurrent: 0,
    closeOnCurrent: 0,
    lastVerdict: null,
    startedAt: now,
    completedAt: null,
  };
}

/**
 * The order exercises are played in: the lesson as authored, then — once each
 * — every testable exercise the learner got WRONG on the first pass, in the
 * order the mistakes happened. Positions ≥ `lesson.exercises.length` are
 * retries. A retry answered wrong again is not asked a third time.
 */
export function playOrder(lesson: Lesson, results: readonly ExerciseResult[]): number[] {
  const n = lesson.exercises.length;
  const order = Array.from({ length: n }, (_, i) => i);
  const firstPass = Math.min(results.length, n);
  for (let i = 0; i < firstPass; i++) {
    if (results[i]?.verdict === 'wrong') order.push(i);
  }
  return order;
}

/** The exercise at play position `index`, or undefined past the end. */
export function exerciseAt(
  lesson: Lesson,
  results: readonly ExerciseResult[],
  index: number,
): Exercise | undefined {
  const i = playOrder(lesson, results)[index];
  return i === undefined ? undefined : lesson.exercises[i];
}

/** True when play position `index` is a second chance at an earlier mistake. */
export function isRetryPosition(lesson: Lesson, index: number): boolean {
  return index >= lesson.exercises.length;
}

/**
 * Pure reducer. `lesson` is threaded in via the outer hook — the reducer
 * stays parameter-free for React semantics but the `lessonLength` is encoded
 * in state via `index === playOrder(...).length` after advancing.
 */
export function lessonReducer(
  state: LessonState,
  action: LessonAction,
  lesson: Lesson,
  now: number = Date.now(),
): LessonState {
  const current = exerciseAt(lesson, state.results, state.index);

  switch (action.type) {
    case 'SUBMIT': {
      // Ignore stale submits when not in the answering phase.
      if (state.phase !== 'answering' || !current) return state;

      if (action.verdict === 'close') {
        const closeOnCurrent = state.closeOnCurrent + 1;
        if (closeOnCurrent < 2) {
          // Transient nudge only — don't count as wrong, don't change phase.
          return { ...state, lastVerdict: 'close', closeOnCurrent };
        }
        // Second near-miss on the same exercise. The nudge never says WHAT is
        // wrong, so a learner who can't spot their own typo retries forever.
        // Close the exercise out as correct — they knew the answer — minus the
        // first-try bonus, and let resultDetail() show the exact spelling.
        return {
          ...state,
          phase: 'checked',
          lastVerdict: 'close',
          closeOnCurrent,
          results: [
            ...state.results,
            { exerciseId: current.id, firstTry: false, verdict: 'correct' },
          ],
        };
      }

      if (action.verdict === 'exact') {
        const result: ExerciseResult = {
          exerciseId: current.id,
          firstTry: state.wrongOnCurrent === 0,
          verdict: 'correct',
        };
        return {
          ...state,
          phase: 'checked',
          lastVerdict: 'exact',
          results: [...state.results, result],
        };
      }

      // `wrong`
      return {
        ...state,
        phase: 'checked',
        lastVerdict: 'wrong',
        wrongOnCurrent: state.wrongOnCurrent + 1,
        results: [
          ...state.results,
          { exerciseId: current.id, firstTry: false, verdict: 'wrong' },
        ],
      };
    }

    case 'MARK_WRONG_ATTEMPT': {
      // Soft-failure marker. tap_pairs uses this to record that a wrong
      // tap occurred during the exercise so the eventual SUBMIT 'exact'
      // (when all pairs are matched) still loses the first-try bonus
      // without flipping the user-visible verdict to 'wrong'. Pre-fix
      // (2026-04-29 phone playthrough), tap_pairs grading verdicts as
      // 'wrong' on any mis-tap was the issue Tornike caught.
      //
      // Only valid during 'answering'. Idempotent on the phase + lastVerdict;
      // bumps wrongOnCurrent so calculateStats / firstTry compute correctly.
      if (state.phase !== 'answering') return state;
      return {
        ...state,
        wrongOnCurrent: state.wrongOnCurrent + 1,
      };
    }

    case 'ADVANCE_INTRO': {
      // grammar_tip, phrase_intro and dialogue are no-fail teaching moments:
      // skip the `checked` phase entirely, append a synthetic correct/first-try
      // result so the index aligns with results, and step to the next
      // exercise (or complete if last).
      if (
        state.phase !== 'answering' ||
        (current?.type !== 'phrase_intro' && current?.type !== 'dialogue' && current?.type !== 'grammar_tip')
      ) return state;
      const result: ExerciseResult = {
        exerciseId: current.id,
        firstTry: true,
        verdict: 'correct',
      };
      const nextIndex = state.index + 1;
      const base = {
        ...state,
        results: [...state.results, result],
        lastVerdict: null,
        wrongOnCurrent: 0,
        closeOnCurrent: 0,
      };
      if (nextIndex >= playOrder(lesson, base.results).length) {
        return { ...base, phase: 'complete', index: nextIndex, completedAt: now };
      }
      return { ...base, phase: 'answering', index: nextIndex };
    }

    case 'NEXT': {
      if (state.phase !== 'checked') return state;
      const nextIndex = state.index + 1;
      // The order may have just grown: a wrong answer adds its retry.
      if (nextIndex >= playOrder(lesson, state.results).length) {
        return {
          ...state,
          phase: 'complete',
          index: nextIndex,
          lastVerdict: null,
          wrongOnCurrent: 0,
          closeOnCurrent: 0,
          completedAt: now,
        };
      }
      return {
        ...state,
        phase: 'answering',
        index: nextIndex,
        lastVerdict: null,
        wrongOnCurrent: 0,
        closeOnCurrent: 0,
      };
    }

    case 'GO_BACK': {
      // Step back to the previous exercise and re-open it for answering.
      // Drop `results` down to exactly `newIndex` entries so the previous
      // exercise's recorded verdict is discarded and re-answering re-records
      // it cleanly. This is the in-lesson back button for a lesson IN PROGRESS
      // — interactive, like normal play. (Read-only "review" of already-learned
      // content is a separate screen, shown only when REOPENING a completed
      // lesson — see LessonRecap.)
      if (state.phase === 'complete' || state.index <= 0) return state;
      const newIndex = state.index - 1;
      return {
        ...state,
        phase: 'answering',
        index: newIndex,
        results: state.results.slice(0, newIndex),
        wrongOnCurrent: 0,
        closeOnCurrent: 0,
        lastVerdict: null,
      };
    }

    case 'RESTART': {
      return initLessonState(lesson, now);
    }

    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
}

/* ----------------------------- XP + stats ----------------------------- */

export interface LessonStats {
  xp: number;
  correctCount: number;
  totalExercises: number;
  /** Accuracy based on the FINAL verdict of each exercise (not counting retries). */
  accuracy: number;
  /** Elapsed seconds between `startedAt` and `completedAt`. */
  elapsedSeconds: number;
  firstTryCount: number;
  perfect: boolean;
}

export const XP_BASE = 10;
export const XP_PERFECT_BONUS = 5;
export const XP_FIRST_TRY = 2;

export function calculateStats(state: LessonState, lesson: Lesson): LessonStats {
  // phrase_intro + dialogue are teaching moments, not tests — drop them
  // from the counts so XP, accuracy, and perfect-run reflect only the
  // real exercises.
  const isTestable = (i: number) => {
    const t = lesson.exercises[i]?.type;
    return t !== 'phrase_intro' && t !== 'dialogue' && t !== 'grammar_tip';
  };
  const totalExercises = lesson.exercises.filter(
    (e) => e.type !== 'phrase_intro' && e.type !== 'dialogue' && e.type !== 'grammar_tip',
  ).length;
  // First pass only — retries of earlier mistakes are practice, not score.
  const testResults = state.results
    .slice(0, lesson.exercises.length)
    .filter((_, i) => isTestable(i));
  const correctCount = testResults.filter((r) => r.verdict === 'correct').length;
  const firstTryCount = testResults.filter((r) => r.firstTry).length;
  const perfect =
    totalExercises > 0 &&
    correctCount === totalExercises &&
    firstTryCount === totalExercises;

  const xp =
    XP_BASE +
    XP_FIRST_TRY * firstTryCount +
    (perfect ? XP_PERFECT_BONUS : 0);

  const completedAt = state.completedAt ?? Date.now();
  const elapsedSeconds = Math.max(0, Math.round((completedAt - state.startedAt) / 1000));
  const accuracy = totalExercises === 0 ? 0 : correctCount / totalExercises;

  return {
    xp,
    correctCount,
    totalExercises,
    accuracy,
    elapsedSeconds,
    firstTryCount,
    perfect,
  };
}
